import { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Table, { Badge, TableLink } from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Toast from "../components/ui/Toast";
import { fetchWithAdminAuth } from "../utils/adminAuth";

export default function Planos() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    duration: "",
    state: "active",
    type: "IA",
    description: "",
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    price: "",
    duration: "",
    state: "active",
    type: "IA",
    description: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const [data, setData] = useState<any[]>([]);

  const fetchPlans = async () => {
    try {
      const response = await fetchWithAdminAuth("/api/plans");
      if (!response.ok) {
        throw new Error("Erro ao carregar planos");
      }

      const payload = await response.json();
      const plans = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.plans)
          ? payload.plans
          : [];

      if (!Array.isArray(plans)) {
        throw new Error("Formato de resposta inesperado em /api/plans");
      }

      setData(
        plans.map((plan: any) => ({
          id: plan.id,
          plan,
          nome: plan.name,
          minutos: plan.duration,
          preco: `€ ${(Number(plan.price) || 0).toFixed(2)}`,
          tipo: plan.type,
          estado: plan.state,
        }))
      );
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : "Erro ao carregar planos",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetchWithAdminAuth("/api/plans", {
        method: "POST",
        body: JSON.stringify({
          name: formData.name,
          price: parseFloat(formData.price),
          duration: parseInt(formData.duration),
          state: formData.state,
          type: formData.type,
          description: formData.description,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erro ao criar plano");
      }

      await response.json();
      await fetchPlans();
      setToast({ message: "Plano criado com sucesso!", type: "success" });
      setIsModalOpen(false);
      setFormData({
        name: "",
        price: "",
        duration: "",
        state: "active",
        type: "IA",
        description: "",
      });
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "Erro ao criar plano",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (row: any) => {
    setEditingPlan(row);
    setEditFormData({
      name: row.plan?.name ?? row.nome ?? "",
      price: String(row.plan?.price ?? row.minutos ?? ""),
      duration: String(row.plan?.duration ?? row.minutos ?? ""),
      state: row.plan?.state ?? row.estado ?? "active",
      type: row.plan?.type ?? row.tipo ?? "IA",
      description: row.plan?.description ?? "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan?.id) {
      setToast({ message: "Plano inválido para edição", type: "error" });
      return;
    }

    setIsUpdating(true);

    try {
      const response = await fetchWithAdminAuth(`/api/plans/${editingPlan.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: editFormData.name,
          price: parseFloat(editFormData.price),
          duration: parseInt(editFormData.duration),
          state: editFormData.state,
          type: editFormData.type,
          description: editFormData.description,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erro ao atualizar plano");
      }

      await fetchPlans();
      setToast({ message: "Plano atualizado com sucesso!", type: "success" });
      setIsEditModalOpen(false);
      setEditingPlan(null);
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : "Erro ao atualizar plano",
        type: "error",
      });
    } finally {
      setIsUpdating(false);
    }
  };


  const columns = [
    { header: "Nome", key: "nome" },
    { header: "Minutos", key: "minutos" },
    { header: "Preço (c/ IVA)", key: "preco" },
    { header: "Tipo", key: "tipo" },
    {
      header: "Estado",
      key: "estado",
      render: (value:any) => (
        <Badge variant={value === "active" ? "success" : "danger"}>
          {value === "active" ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      header: "",
      key: "actions",
      render: (_value:any, row:any) => (
        <TableLink onClick={() => openEditModal(row)}>Editar</TableLink>
      ),
    },
  ];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Criar Novo Plano"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-white mb-2"
            >
              Nome
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Nome do plano"
            />
          </div>

          <div>
            <label
              htmlFor="price"
              className="block text-sm font-medium text-white mb-2"
            >
              Preço (€)
            </label>
            <input
              type="number"
              id="price"
              step="0.01"
              value={formData.price}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="12.00"
            />
          </div>

          <div>
            <label
              htmlFor="duration"
              className="block text-sm font-medium text-white mb-2"
            >
              Duração (minutos)
            </label>
            <input
              type="number"
              id="duration"
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="10"
            />
          </div>

          <div>
            <label
              htmlFor="state"
              className="block text-sm font-medium text-white mb-2"
            >
              Estado
            </label>
            <select
              id="state"
              value={formData.state}
              onChange={(e) =>
                setFormData({ ...formData, state: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="active" className="bg-gray-800">
                Ativo
              </option>
              <option value="inactive" className="bg-gray-800">
                Inativo
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="type"
              className="block text-sm font-medium text-white mb-2"
            >
              Tipo
            </label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="IA" className="bg-gray-800">
                IA
              </option>
              <option value="person" className="bg-gray-800">
                Pessoa
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-white mb-2"
            >
              Descrição (texto por debaixo do preço)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Ex: Consulta com inteligência artificial"
            />
            <p className="mt-1 text-xs text-gray-500">Se vazio, será usado o texto padrão baseado no tipo.</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="primary" disabled={isLoading}>
              {isLoading ? "A criar..." : "Criar Plano"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Plano"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label
              htmlFor="edit-name"
              className="block text-sm font-medium text-white mb-2"
            >
              Nome
            </label>
            <input
              type="text"
              id="edit-name"
              value={editFormData.name}
              onChange={(e) =>
                setEditFormData({ ...editFormData, name: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Nome do plano"
            />
          </div>

          <div>
            <label
              htmlFor="edit-price"
              className="block text-sm font-medium text-white mb-2"
            >
              Preço (€)
            </label>
            <input
              type="number"
              id="edit-price"
              step="0.01"
              value={editFormData.price}
              onChange={(e) =>
                setEditFormData({ ...editFormData, price: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="12.00"
            />
          </div>

          <div>
            <label
              htmlFor="edit-duration"
              className="block text-sm font-medium text-white mb-2"
            >
              Duração (minutos)
            </label>
            <input
              type="number"
              id="edit-duration"
              value={editFormData.duration}
              onChange={(e) =>
                setEditFormData({ ...editFormData, duration: e.target.value })
              }
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="10"
            />
          </div>

          <div>
            <label
              htmlFor="edit-state"
              className="block text-sm font-medium text-white mb-2"
            >
              Estado
            </label>
            <select
              id="edit-state"
              value={editFormData.state}
              onChange={(e) =>
                setEditFormData({ ...editFormData, state: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="active" className="bg-gray-800">
                Ativo
              </option>
              <option value="inactive" className="bg-gray-800">
                Inativo
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="edit-type"
              className="block text-sm font-medium text-white mb-2"
            >
              Tipo
            </label>
            <select
              id="edit-type"
              value={editFormData.type}
              onChange={(e) =>
                setEditFormData({ ...editFormData, type: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="IA" className="bg-gray-800">
                IA
              </option>
              <option value="person" className="bg-gray-800">
                Pessoa
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="edit-description"
              className="block text-sm font-medium text-white mb-2"
            >
              Descrição (texto por debaixo do preço)
            </label>
            <textarea
              id="edit-description"
              value={editFormData.description}
              onChange={(e) =>
                setEditFormData({ ...editFormData, description: e.target.value })
              }
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Ex: Consulta com inteligência artificial"
            />
            <p className="mt-1 text-xs text-gray-500">Se vazio, será usado o texto padrão baseado no tipo.</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="primary" disabled={isUpdating}>
              {isUpdating ? "A atualizar..." : "Atualizar"}
            </Button>
          </div>
        </form>
      </Modal>

      <Card
        title="Planos & Cupões"
        subtitle="Configure pacotes de minutos e códigos promocionais."
        action={
          <Button variant="primary" onClick={() => setIsModalOpen(true)}>
            + Novo plano
          </Button>
        }
      >
        <Table columns={columns} data={data} />
      </Card>
    </div>
  );
}
